import {getScrollableAncestor} from '@albert-io/atomic/helpers/AnchoredFixedPosition/AnchoredFixedPosition.react';

import {ANNOTATION_INDICATOR_PANE_ATTRIBUTE} from './annotationGutter';

/** Positions that make an element the containing block of its absolute children. */
const CONTAINING_BLOCK_POSITIONS = new Set(['relative', 'absolute', 'fixed', 'sticky']);

function isContainingBlock(pane: HTMLElement): boolean {
  return CONTAINING_BLOCK_POSITIONS.has(getComputedStyle(pane).position);
}

/**
 * The pane a margin indicator portals into: the nearest scrolling ancestor marked
 * as an indicator pane — skipping content-local scrollers (a wide table's overflow
 * wrapper) that would trap it on the text (#18667) — else the nearest scrolling
 * ancestor. Must be a containing block, or the indicator tracks neither content
 * nor scroll.
 */
export function resolveIndicatorScroller(element: HTMLElement | null): HTMLElement | null {
  const nearest = (element && getScrollableAncestor(element)) ?? null;
  for (let scroller = nearest; scroller; scroller = getScrollableAncestor(scroller) ?? null) {
    if (scroller.hasAttribute(ANNOTATION_INDICATOR_PANE_ATTRIBUTE) && isContainingBlock(scroller)) {
      return scroller;
    }
  }
  return nearest && isContainingBlock(nearest) ? nearest : null;
}

/** Viewport y to scrolled-content y: past the border, then past what's scrolled off. */
export function scrollerContentOffset(scroller: HTMLElement): number {
  return scroller.scrollTop - scroller.getBoundingClientRect().top - scroller.clientTop;
}

/** `element` and its ancestors, up to and including `ancestor`. */
export function selfAndAncestorsThrough(element: Element, ancestor: Element): Element[] {
  const chain: Element[] = [];
  for (let current: Element | null = element; current; current = current.parentElement) {
    chain.push(current);
    if (current === ancestor) {
      break;
    }
  }
  return chain;
}
